import { useFleetUnits, fleetStatusSkin, type FleetUnitRow } from "../../screens/trucking/FleetRegistryScreen";
import { queryLoadFailure } from "../../lib/load-failure";

/**
 * The truck picker for a load or a weight ticket: the tenant's fleet units,
 * from GET /trucking/fleet (`trucking_fleet`).
 *
 * Since api#860 (migration 0434) `loads.truck_id`, `weight_tickets.truck_id`
 * and `master_bol_loads.truck_id` reference `trucking_fleet`. Before that they
 * pointed at the legacy `trucks` table, which is empty for every tenant, so no
 * screen could put a truck on either. A legacy `trucks` id now answers 404.
 *
 * Retired units are not listed (the route leaves them out). For a LOAD, a unit
 * that is `out_of_service` is listed but cannot be chosen: the API refuses it
 * with a 409 until the office certifies the repair (lib/fleet-dispatchable.ts).
 * A weight ticket records a weighing, not dispatch, and the API takes any unit
 * the tenant owns, so it lists them all.
 *
 * The truck is optional on both. A list that failed to load says so and leaves
 * "No truck" as the only choice, rather than reading as an empty fleet.
 */
export type FleetUnitPurpose = "load" | "weight-ticket";
/**
 * Which units a picker offers (api#1045): `truck` lists the power units and
 * `trailer` the trailers, each from GET /trucking/fleet?type=…. Omitted, every
 * unit is listed, which is what a weight ticket has always offered.
 */
export type FleetUnitKind = "truck" | "trailer";

const OUT_OF_SERVICE = "out_of_service";

/** May this unit be chosen for this purpose? Mirrors the API's refusal for loads. */
export const fleetUnitSelectable = (u: FleetUnitRow, purpose: FleetUnitPurpose) =>
  purpose === "weight-ticket" || (u.status !== OUT_OF_SERVICE && u.status !== "retired");

/** "T-104 · Vac · Pat Keller", missing parts left out; a unit with no number is a dash. */
export function fleetUnitLabel(u: FleetUnitRow): string {
  return [u.unit_number ?? "—", u.type, u.driver].filter(Boolean).join(" · ");
}

interface Props {
  id: string;
  value: string;
  onChange: (fleetUnitId: string) => void;
  purpose: FleetUnitPurpose;
  kind?: FleetUnitKind;
  className?: string;
}

export default function FleetUnitPicker({ id, value, onChange, purpose, kind, className }: Props) {
  const query = useFleetUnits(false, kind);
  const failed = queryLoadFailure(query) != null;
  const units = failed ? undefined : query.data;
  const noun = purpose === "load" ? "load" : "ticket";
  const thing = kind === "trailer" ? "trailer" : "truck";
  const listName = kind === "trailer" ? "trailer list" : "fleet list";

  return (
    <>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={query.isLoading && !failed}
        className={className}
      >
        <option value="">{kind === "trailer" ? "No trailer" : "No truck"}</option>
        {(units ?? []).map((u) => {
          const selectable = fleetUnitSelectable(u, purpose);
          return (
            <option key={u.id} value={u.id} disabled={!selectable}>
              {fleetUnitLabel(u)}
              {!selectable ? ` (${fleetStatusSkin(u.status).label.toLowerCase()})` : ""}
            </option>
          );
        })}
      </select>
      {failed ? (
        <p role="status" className="mt-1 text-[11px] text-fieldiq-muted">
          The {listName} could not be loaded. The {noun} can be saved without a {thing}.
        </p>
      ) : units && units.length === 0 ? (
        <p className="mt-1 text-[11px] text-fieldiq-muted">
          {kind === "trailer" ? "No trailers in the fleet registry." : "No units in the fleet registry."} The {noun} can be saved without a {thing}.
        </p>
      ) : null}
    </>
  );
}

/** Look a chosen unit up in the shared fleet query, for a confirmation summary. */
export function useFleetUnit(fleetUnitId: string, kind?: FleetUnitKind): FleetUnitRow | null {
  const query = useFleetUnits(false, kind);
  if (!fleetUnitId || queryLoadFailure(query) != null) return null;
  return query.data?.find((u) => u.id === fleetUnitId) ?? null;
}
